/**
 * DualTransferList Component
 * Generic reusable component for transferring items between two lists
 * Used for applying/removing exams, risks, and source generators
 *
 * @version 2025.1.0
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  KeyboardDoubleArrowRight as DoubleRightIcon,
  KeyboardDoubleArrowLeft as DoubleLeftIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

// ============================================================================
// TYPES
// ============================================================================

/** De onde veio o lote enviado ao onBeforeApply. */
export interface ContextoDaAplicacao {
  /** Veio de "aplicar todos", que leva também o que a busca não mostra. */
  todos: boolean;
  /** Quantos itens do lote a busca está escondendo. */
  escondidosPelaBusca: number;
}

export interface DualTransferListProps<T> {
  /** Items available for selection (left side) */
  available: T[];
  /** Items already applied/selected (right side) */
  applied: T[];
  /** Callback when items are transferred to applied. The transfer buttons stay locked until it settles. */
  onApply: (items: T[]) => void | Promise<void>;
  /** Callback when items are removed from applied */
  onRemove: (items: T[]) => void;
  /** Function to get unique key for each item */
  getItemKey: (item: T) => string | number;
  /** Function to get display text for each item */
  getItemLabel: (item: T) => string;
  /** Function to get secondary text (optional) */
  getItemSecondary?: (item: T) => string | undefined;
  /** Fields to search in (array of keys) */
  searchFields: (keyof T)[];
  /** Title for available list */
  availableTitle?: string;
  /** Title for applied list */
  appliedTitle?: string;
  /** Loading state */
  loading?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Custom render function for items (optional) */
  renderItem?: (item: T, isApplied: boolean) => React.ReactNode;
  /** Height of the lists */
  listHeight?: number | string;
  /** Callback before applying (for confirmation/modal) */
  onBeforeApply?: (items: T[], contexto: ContextoDaAplicacao) => Promise<boolean>;
  /** Callback when edit button is clicked on an applied item (navega para o cadastro do exame) */
  onItemEdit?: (item: T) => void;
  /** Callback when the configure (gear) button is clicked on an applied item (abre detalhes da aplicação) */
  onItemConfigure?: (item: T) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function not<T>(a: T[], b: T[], getKey: (item: T) => string | number): T[] {
  const bKeys = new Set(b.map(getKey));
  return a.filter((item) => !bKeys.has(getKey(item)));
}

function intersection<T>(a: T[], b: T[], getKey: (item: T) => string | number): T[] {
  const bKeys = new Set(b.map(getKey));
  return a.filter((item) => bKeys.has(getKey(item)));
}

// ============================================================================
// COMPONENT
// ============================================================================

export function DualTransferList<T>({
  available,
  applied,
  onApply,
  onRemove,
  getItemKey,
  getItemLabel,
  getItemSecondary,
  searchFields,
  availableTitle = 'Disponíveis',
  appliedTitle = 'Aplicados',
  loading = false,
  disabled = false,
  renderItem,
  listHeight = 400,
  onBeforeApply,
  onItemEdit,
  onItemConfigure,
}: DualTransferListProps<T>): React.ReactElement {
  // State
  const [checked, setChecked] = useState<T[]>([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Computed
  const leftChecked = useMemo(
    () => intersection(checked, available, getItemKey),
    [checked, available, getItemKey]
  );

  const rightChecked = useMemo(
    () => intersection(checked, applied, getItemKey),
    [checked, applied, getItemKey]
  );

  // Filter function
  const filterItems = useCallback(
    (items: T[], searchTerm: string): T[] => {
      if (!searchTerm.trim()) return items;

      const term = searchTerm.toLowerCase();
      return items.filter((item) =>
        searchFields.some((field) => {
          const value = item[field];
          if (typeof value === 'string') {
            return value.toLowerCase().includes(term);
          }
          if (typeof value === 'number') {
            return value.toString().includes(term);
          }
          return false;
        })
      );
    },
    [searchFields]
  );

  const filteredAvailable = useMemo(
    () => filterItems(available, leftSearch),
    [available, leftSearch, filterItems]
  );

  const filteredApplied = useMemo(
    () => filterItems(applied, rightSearch),
    [applied, rightSearch, filterItems]
  );

  // Handlers
  const handleToggle = useCallback(
    (item: T) => {
      const currentIndex = checked.findIndex(
        (c) => getItemKey(c) === getItemKey(item)
      );
      const newChecked = [...checked];

      if (currentIndex === -1) {
        newChecked.push(item);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setChecked(newChecked);
    },
    [checked, getItemKey]
  );

  const handleToggleAll = useCallback(
    (items: T[]) => {
      const allChecked = items.every((item) =>
        checked.some((c) => getItemKey(c) === getItemKey(item))
      );

      if (allChecked) {
        // Uncheck all
        setChecked(not(checked, items, getItemKey));
      } else {
        // Check all
        const newChecked = [...checked];
        items.forEach((item) => {
          if (!newChecked.some((c) => getItemKey(c) === getItemKey(item))) {
            newChecked.push(item);
          }
        });
        setChecked(newChecked);
      }
    },
    [checked, getItemKey]
  );

  const handleApplyChecked = useCallback(async () => {
    if (leftChecked.length === 0) return;

    setTransferring(true);
    try {
      // Check if we need confirmation
      if (onBeforeApply) {
        const escondidosPelaBusca = leftChecked.filter(
          (item) => !filteredAvailable.some((visivel) => getItemKey(visivel) === getItemKey(item))
        ).length;
        const proceed = await onBeforeApply(leftChecked, { todos: false, escondidosPelaBusca });
        if (!proceed) {
          setTransferring(false);
          return;
        }
      }

      await onApply(leftChecked);
      setChecked((atual) => not(atual, leftChecked, getItemKey));
    } finally {
      setTransferring(false);
    }
  }, [leftChecked, filteredAvailable, onApply, onBeforeApply, getItemKey]);

  const handleRemoveChecked = useCallback(() => {
    if (rightChecked.length === 0) return;

    onRemove(rightChecked);
    setChecked(not(checked, rightChecked, getItemKey));
  }, [rightChecked, onRemove, checked, getItemKey]);

  const handleApplyAll = useCallback(async () => {
    if (available.length === 0) return;

    setTransferring(true);
    try {
      if (onBeforeApply) {
        const proceed = await onBeforeApply(available, {
          todos: true,
          escondidosPelaBusca: available.length - filteredAvailable.length,
        });
        if (!proceed) {
          setTransferring(false);
          return;
        }
      }

      await onApply(available);
      setChecked((atual) => not(atual, available, getItemKey));
    } finally {
      setTransferring(false);
    }
  }, [available, filteredAvailable, onApply, onBeforeApply, getItemKey]);

  const handleRemoveAll = useCallback(() => {
    if (applied.length === 0) return;

    onRemove(applied);
    setChecked(not(checked, applied, getItemKey));
  }, [applied, onRemove, checked, getItemKey]);

  // Check states
  const numberOfLeftChecked = leftChecked.length;
  const numberOfRightChecked = rightChecked.length;

  const isAllLeftChecked =
    filteredAvailable.length > 0 &&
    filteredAvailable.every((item) =>
      checked.some((c) => getItemKey(c) === getItemKey(item))
    );

  const isAllRightChecked =
    filteredApplied.length > 0 &&
    filteredApplied.every((item) =>
      checked.some((c) => getItemKey(c) === getItemKey(item))
    );

  const isSomeLeftChecked =
    !isAllLeftChecked &&
    filteredAvailable.some((item) =>
      checked.some((c) => getItemKey(c) === getItemKey(item))
    );

  const isSomeRightChecked =
    !isAllRightChecked &&
    filteredApplied.some((item) =>
      checked.some((c) => getItemKey(c) === getItemKey(item))
    );

  // Render list
  const renderList = (
    title: string,
    items: T[],
    filteredItems: T[],
    search: string,
    setSearch: (value: string) => void,
    isApplied: boolean,
    isAllChecked: boolean,
    isSomeChecked: boolean
  ) => (
    <Paper
      elevation={2}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: isApplied ? 'success.50' : 'primary.50',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredItems.length} / {items.length}
          </Typography>
        </Stack>
      </Box>

      {/* Search */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled || loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Select All */}
      <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
        <ListItemButton
          dense
          onClick={() => handleToggleAll(filteredItems)}
          disabled={disabled || loading || filteredItems.length === 0}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Checkbox
              edge="start"
              checked={isAllChecked}
              indeterminate={isSomeChecked}
              disabled={disabled || loading || filteredItems.length === 0}
              size="small"
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" fontWeight={500}>
                Selecionar todos
              </Typography>
            }
          />
        </ListItemButton>
      </Box>

      {/* List */}
      <List
        dense
        sx={{
          flex: 1,
          overflow: 'auto',
          height: listHeight,
          bgcolor: 'background.paper',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {search ? 'Nenhum item encontrado' : 'Nenhum item disponível'}
            </Typography>
          </Box>
        ) : (
          filteredItems.map((item) => {
            const key = getItemKey(item);
            const isChecked = checked.some((c) => getItemKey(c) === key);

            return (
              <ListItem
                key={key}
                disablePadding
                secondaryAction={
                  isApplied && (onItemConfigure || onItemEdit) ? (
                    <Stack direction="row" spacing={0.5}>
                      {onItemConfigure && (
                        <Tooltip title="Configurar aplicação">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onItemConfigure(item);
                            }}
                            aria-label="Configurar aplicação"
                            disabled={disabled}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onItemEdit && (
                        <Tooltip title="Editar cadastro do exame">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onItemEdit(item);
                            }}
                            aria-label="Editar cadastro do exame"
                            disabled={disabled}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  ) : undefined
                }
              >
                <ListItemButton
                  onClick={() => handleToggle(item)}
                  disabled={disabled}
                  dense
                  sx={
                    isApplied && (onItemConfigure || onItemEdit)
                      ? { pr: onItemConfigure && onItemEdit ? 11 : 6 }
                      : undefined
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={isChecked}
                      disabled={disabled}
                      size="small"
                    />
                  </ListItemIcon>
                  {renderItem ? (
                    renderItem(item, isApplied)
                  ) : (
                    <ListItemText
                      primary={getItemLabel(item)}
                      secondary={getItemSecondary?.(item)}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
      {/* Left List - Available */}
      {renderList(
        availableTitle,
        available,
        filteredAvailable,
        leftSearch,
        setLeftSearch,
        false,
        isAllLeftChecked,
        isSomeLeftChecked
      )}

      {/* Transfer Buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Tooltip title="Aplicar todos" placement="right">
          <span>
            <IconButton
              onClick={handleApplyAll}
              disabled={disabled || loading || transferring || available.length === 0}
              color="primary"
              size="small"
            >
              <DoubleRightIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={`Aplicar selecionados (${numberOfLeftChecked})`} placement="right">
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyChecked}
              disabled={disabled || loading || transferring || numberOfLeftChecked === 0}
              sx={{ minWidth: 40, px: 1 }}
            >
              {transferring ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ChevronRightIcon />
              )}
            </Button>
          </span>
        </Tooltip>

        <Divider sx={{ my: 1 }} />

        <Tooltip title={`Remover selecionados (${numberOfRightChecked})`} placement="right">
          <span>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRemoveChecked}
              disabled={disabled || loading || transferring || numberOfRightChecked === 0}
              color="error"
              sx={{ minWidth: 40, px: 1 }}
            >
              <ChevronLeftIcon />
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Remover todos" placement="right">
          <span>
            <IconButton
              onClick={handleRemoveAll}
              disabled={disabled || loading || transferring || applied.length === 0}
              color="error"
              size="small"
            >
              <DoubleLeftIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Right List - Applied */}
      {renderList(
        appliedTitle,
        applied,
        filteredApplied,
        rightSearch,
        setRightSearch,
        true,
        isAllRightChecked,
        isSomeRightChecked
      )}
    </Box>
  );
}

export default DualTransferList;
